// An online-play session as the host page runs it (plan.md §19, spec §4.12): the room (signalling over the room
// server's WebSocket), one WebRTC connection per other player (rtc.ts) in a mesh (mesh.ts), the build/ROM check
// between peers, the lobby's round trips, and the session start. It outlives the Player (which restarts for a
// lockstep boot), so the game page owns it; the Player attaches a sink that relays packets to the game's worker.
import {
  checkFromRoom, matchKeys, NET, pairTable, PEER_CONNECTED, PEER_GONE, PEER_SILENT, SEND_NO_PEER, SEND_OK, BLOB_CHUNK,
  type Ctl, type FromRoom, type NetModel, type PingRow, type RoomEnd, type RoomRefusal, type ToRoom,
} from '../../shared/net.ts';
import type { NetStart } from '../../shared/protocol.ts';
import { Mesh } from './mesh.ts';
import { rtcPeer, type RtcPeer } from './rtc.ts';

export type NetPhase = 'idle' | 'connecting' | 'lobby' | 'session';

export interface LobbyPeer {
  id: number;
  /** the peer's build id and ROM SHA-1 against ours, from its hello over our own connection */
  match: 'pending' | 'ok' | 'build' | 'rom';
  state: number; // -1 connecting, else PEER_*
  rtt: number;
  hidden: boolean;
  direct: boolean;
}

export interface BootInfo {
  model: NetModel;
  /** room ids in session order; index 0 is the room host */
  ids: number[];
  /** lockstep: the room host's option values and save, which every peer boots with */
  options: [number, number][];
  save?: Uint8Array;
  start: NetStart;
}

/** Where packets for the game go (the Player relays them over the net port). Indexes, not room ids. */
export interface NetSink {
  packet(index: number, ch: number, bytes: ArrayBuffer): void;
  peer(index: number, state: number): void;
  rtt(rtt: number[]): void;
  /** model "link": the session starts while the game runs */
  session(start: NetStart): void;
}

export interface NetDeps {
  buildId: string;
  romSha1: string;
  players: number;
  model: NetModel;
  /** WebSocket URL of /api/room/<path> */
  roomUrl(path: string): string;
  /** The room host's current option values and native save (lockstep boot). */
  local(): { options: [number, number][]; save?: Uint8Array };
  /** lockstep: reboot the game with this boot. */
  onBoot(b: BootInfo): void;
  /** lockstep: the player left the session; restart the game alone. */
  onEnd(): void;
}

const REFUSED: Record<RoomRefusal, string> = {
  notfound: 'There is no room with that code (rooms last 10 minutes).',
  closed: 'That room has already started or closed.',
  full: 'That room is full.',
  build: 'That room plays a different version of this game. Everyone needs the same build: reload the game page to get the latest one.',
  rom: 'That room uses a different ROM (its fingerprint differs from yours).',
  bad: 'The room server refused the connection.',
  busy: 'The room server is busy; try again.',
};
const ENDED: Record<RoomEnd, string> = {
  started: 'The session started.',
  expired: 'The room expired (rooms last 10 minutes).',
  'host-left': 'The room host left.',
};

export class NetSession {
  phase: NetPhase = 'idle';
  code: string | null = null;
  me = -1;
  hostId = -1;
  max = 0;
  error = '';
  boot: BootInfo | null = null;
  private deps: NetDeps;
  private ws: WebSocket | null = null;
  private creating = false;
  private mesh: Mesh | null = null;
  private rtcs = new Map<number, RtcPeer>();
  private lobby = new Map<number, LobbyPeer>();
  private pingRows = new Map<number, PingRow[]>();
  private pendingBoot: { msg: Extract<Ctl, { t: 'boot' }>; buf: Uint8Array; got: number } | null = null;
  private sink: NetSink | null = null;
  private queue: [number, number, ArrayBuffer][] = [];
  private announced = false;
  private timer: ReturnType<typeof setInterval> | null = null;
  private lastRows = 0;
  private lastRtt = 0;
  private lastUi = 0;
  private hidden = false;
  private listeners = new Set<() => void>();

  constructor(deps: NetDeps) { this.deps = deps; }

  subscribe(f: () => void): () => void { this.listeners.add(f); return () => this.listeners.delete(f); }
  private changed() { for (const f of this.listeners) f(); }

  isHost(): boolean { return this.me >= 0 && this.me === this.hostId; }
  inSession(): boolean { return this.phase === 'session'; }
  peers(): LobbyPeer[] { return [...this.lobby.values()].sort((a, b) => a.id - b.id); }
  /** Player number shown to people (1 = room host): session index + 1, or join order in the room. */
  playerNumber(id: number): number {
    if (this.boot) return this.boot.ids.indexOf(id) + 1;
    return [this.hostId, ...[this.me, ...this.lobby.keys()].filter((x) => x !== this.hostId).sort((a, b) => a - b)].indexOf(id) + 1;
  }
  /** Guests ready to play: same build and ROM, connected. */
  ready(): LobbyPeer[] { return this.peers().filter((p) => p.match === 'ok' && p.state === PEER_CONNECTED); }
  canStart(): boolean { return this.phase === 'lobby' && this.isHost() && this.ready().length >= 1; }
  hiddenPlayers(): number[] { return this.peers().filter((p) => p.hidden && p.state !== PEER_GONE).map((p) => this.playerNumber(p.id)); }

  create() { this.connect('new', true); }
  join(code: string) { this.connect(code, false); }

  private connect(path: string, creating: boolean) {
    if (this.phase !== 'idle') return;
    this.reset();
    this.error = '';
    this.creating = creating;
    this.phase = 'connecting';
    let ws: WebSocket;
    try { ws = new WebSocket(this.deps.roomUrl(path)); } catch { return this.fail('Could not reach the room server.'); }
    this.ws = ws;
    ws.onmessage = (e) => { if (this.ws === ws) { const m = checkFromRoom(e.data); if (m) void this.fromRoom(m); } };
    ws.onclose = () => {
      if (this.ws !== ws) return;
      this.ws = null;
      if (this.phase === 'connecting') this.fail('Could not reach the room server.');
    };
    this.timer = setInterval(() => this.tick(), 100);
    this.changed();
  }

  private toRoom(m: ToRoom) { if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(m)); }

  private async fromRoom(m: FromRoom) {
    switch (m.t) {
      case 'room': {
        this.code = m.code;
        const keys = await matchKeys(m.code, this.deps.buildId, this.deps.romSha1);
        this.toRoom({ t: 'hello', ...keys, ...(this.creating ? { max: Math.min(NET.maxPlayers, Math.max(2, this.deps.players)) } : {}) });
        break;
      }
      case 'welcome':
        this.me = m.you; this.hostId = m.host; this.max = m.max;
        this.mesh = new Mesh(m.you, m.host, {
          packet: (from, ch, bytes) => this.packet(from, ch, bytes),
          ctl: (from, msg) => this.ctl(from, msg),
          blob: (from, offset, total, bytes) => this.blob(from, offset, total, bytes),
          state: (id, s) => this.peerState(id, s),
        });
        this.phase = 'lobby';
        for (const id of m.peers) this.addPeer(id, true); // the newer peer offers
        break;
      case 'join': if (this.phase === 'lobby') this.addPeer(m.id, false); break;
      case 'left':
        if (this.phase === 'lobby') { this.mesh?.gone(m.id); this.lobby.delete(m.id); this.rtcs.delete(m.id); }
        break;
      case 'signal': await this.rtcs.get(m.from)?.signal(m.data); break;
      case 'refused': this.fail(REFUSED[m.reason]); break;
      case 'closed':
        this.ws?.close();
        this.ws = null;
        // "started": the room host's boot arrives over our own connection (maybe a moment later)
        if (m.reason !== 'started' && this.phase !== 'session') this.fail(ENDED[m.reason]);
        break;
    }
    this.changed();
  }

  private addPeer(id: number, offerer: boolean) {
    if (!this.mesh || id === this.me || this.rtcs.has(id)) return;
    this.lobby.set(id, { id, match: 'pending', state: -1, rtt: -1, hidden: false, direct: false });
    const mesh = this.mesh;
    const rtc: RtcPeer = rtcPeer(offerer, {
      sendSignal: (data) => this.toRoom({ t: 'signal', to: id, data }),
      message: (reliable, bytes) => mesh.receive(id, reliable, bytes),
      open: () => {
        mesh.sendCtl(id, { t: 'hello', v: 1, build: this.deps.buildId, rom: this.deps.romSha1 });
        this.changed();
      },
      closed: () => { mesh.linkClosed(id, rtc.link); this.changed(); },
    });
    this.rtcs.set(id, rtc);
    mesh.setLink(id, rtc.link);
  }

  // ---- mesh events -----------------------------------------------------------------------------------

  private indexOf(id: number): number { return this.boot ? this.boot.ids.indexOf(id) : -1; }

  private packet(from: number, ch: number, bytes: Uint8Array) {
    const i = this.indexOf(from);
    if (this.phase !== 'session' || i < 0) return;
    const ab = bytes.slice().buffer as ArrayBuffer;
    if (this.sink) this.sink.packet(i, ch, ab);
    else if (this.queue.length < NET.queueMax) this.queue.push([i, ch, ab]);
  }

  private ctl(from: number, msg: Ctl) {
    const p = this.lobby.get(from);
    switch (msg.t) {
      case 'hello':
        if (!p) return;
        p.match = msg.build !== this.deps.buildId ? 'build' : msg.rom !== this.deps.romSha1 ? 'rom' : 'ok';
        if (p.match !== 'ok') this.mesh?.gone(from); // the room server checks this too; never play with a mismatch
        break;
      case 'pings': if (this.isHost() && p) this.pingRows.set(from, msg.rows); break;
      case 'boot':
        if (from !== this.hostId || this.phase !== 'lobby' || this.pendingBoot) return;
        if (msg.model === 'lockstep' && msg.save > 0) this.pendingBoot = { msg, buf: new Uint8Array(msg.save), got: 0 };
        else this.begin(msg, undefined);
        break;
      case 'vis': if (p) p.hidden = msg.hidden; break;
      case 'bye': this.mesh?.gone(from); break;
    }
    this.changed();
  }

  private blob(from: number, offset: number, total: number, bytes: Uint8Array) {
    const b = this.pendingBoot;
    if (!b || from !== this.hostId || total !== b.buf.length) return;
    b.buf.set(bytes, offset);
    b.got += bytes.length;
    if (b.got >= total) { this.pendingBoot = null; this.begin(b.msg, b.buf); }
  }

  private peerState(id: number, s: number) {
    const p = this.lobby.get(id);
    if (p) { p.state = s; p.direct = !!this.mesh?.direct(id); }
    if (this.phase === 'lobby' && id === this.hostId && s === PEER_GONE) this.fail(ENDED['host-left']);
    const i = this.indexOf(id);
    if (this.phase === 'session' && i >= 0) this.sink?.peer(i, s);
    this.changed();
  }

  private tick() {
    const mesh = this.mesh;
    if (!mesh) return;
    mesh.tick();
    const now = performance.now();
    for (const p of this.lobby.values()) { p.rtt = Math.round(mesh.rttOf(p.id)); p.direct = mesh.direct(p.id); }
    // guests tell the room host their round trips, for the boot's shared table
    if (this.phase === 'lobby' && !this.isHost() && now - this.lastRows > 1000) {
      this.lastRows = now;
      this.mesh?.sendCtl(this.hostId, { t: 'pings', rows: this.rows() });
    }
    if (this.phase === 'session' && this.boot && this.sink && now - this.lastRtt > NET.pingMs) {
      this.lastRtt = now;
      this.sink.rtt(this.boot.ids.map((id) => (id === this.me ? 0 : Math.round(mesh.rttOf(id)))));
    }
    if (now - this.lastUi > 500) { this.lastUi = now; this.changed(); } // round trips in the panel
  }

  private rows(): PingRow[] {
    const out: PingRow[] = [];
    for (const id of this.mesh?.ids() ?? []) {
      const s = this.mesh!.summary(id);
      if (s) out.push([id, Math.min(60000, s.median), Math.min(60000, s.p90)]);
    }
    return out;
  }

  // ---- starting -------------------------------------------------------------------------------------

  /** Room host: start the session with every ready guest (lockstep: everyone reboots with our options and save). */
  start() {
    if (!this.canStart() || !this.mesh) return;
    const guests = this.ready().slice(0, Math.max(1, this.deps.players - 1)).map((p) => p.id);
    const ids = [this.me, ...guests];
    const rows = new Map(this.pingRows);
    rows.set(this.me, this.rows());
    const local = this.deps.model === 'lockstep' ? this.deps.local() : { options: [] as [number, number][], save: undefined };
    const save = local.save && local.save.length <= NET.saveMax ? local.save : undefined;
    const msg: Extract<Ctl, { t: 'boot' }> = { t: 'boot', model: this.deps.model, ids, options: local.options, save: save?.length ?? 0, pairs: pairTable(ids, rows) };
    for (const g of guests) {
      this.mesh.sendCtl(g, msg);
      if (save) for (let o = 0; o < save.length; o += BLOB_CHUNK) this.mesh.sendBlob(g, o, save.length, save.subarray(o, o + BLOB_CHUNK));
    }
    this.toRoom({ t: 'start' }); // the room closes; nobody else can join
    this.begin(msg, save);
  }

  private begin(msg: Extract<Ctl, { t: 'boot' }>, save: Uint8Array | undefined) {
    const me = msg.ids.indexOf(this.me);
    if (me < 0) return this.fail('The session started without you.');
    // whoever isn't in the session is dropped
    for (const id of this.mesh?.ids() ?? []) if (!msg.ids.includes(id)) { this.mesh?.sendCtl(id, { t: 'bye' }); this.mesh?.gone(id); }
    const idx = (id: number) => msg.ids.indexOf(id);
    const pairs = msg.pairs.filter(([a, b]) => idx(a) >= 0 && idx(b) >= 0).map(([a, b, m, p]) => [idx(a), idx(b), m, p] as [number, number, number, number]);
    const start: NetStart = {
      players: msg.ids.length, me, model: msg.model,
      peers: msg.ids.map((id) => (id === this.me ? PEER_CONNECTED : this.mesh?.stateOf(id) === PEER_CONNECTED ? PEER_CONNECTED : PEER_SILENT)),
      pairs, worst: pairs.length ? Math.max(...pairs.map((x) => x[3])) : -1,
    };
    this.boot = { model: msg.model, ids: msg.ids, options: msg.options, save, start };
    this.phase = 'session';
    this.queue = [];
    this.ws?.close();
    this.ws = null;
    this.announced = false;
    if (msg.model === 'lockstep') this.deps.onBoot(this.boot);
    else this.announce();
    if (this.hidden) this.broadcastVis();
    this.changed();
  }

  private announce() {
    if (this.sink && this.boot && this.boot.model === 'link' && !this.announced) {
      this.announced = true;
      this.sink.session(this.boot.start);
    }
  }

  // ---- the game side ---------------------------------------------------------------------------------

  attach(sink: NetSink) {
    this.sink = sink;
    for (const [i, ch, b] of this.queue.splice(0)) sink.packet(i, ch, b);
    this.announce();
  }

  detach(sink: NetSink) { if (this.sink === sink) this.sink = null; }

  /** A packet from the game: to session index `index`, or -1 for every other peer. */
  sendGame(index: number, ch: number, bytes: Uint8Array): number {
    if (this.phase !== 'session' || !this.boot || !this.mesh) return SEND_NO_PEER;
    const targets = index === -1 ? this.boot.ids.filter((id) => id !== this.me) : [this.boot.ids[index]];
    let result = SEND_NO_PEER;
    for (const id of targets) {
      if (id === undefined || id === this.me) continue;
      const r = this.mesh.sendGame(id, ch, bytes);
      if (r === SEND_OK || result === SEND_NO_PEER) result = r;
    }
    return result;
  }

  /** The tab was hidden or shown: the others are told (a hidden tab may stall everyone). */
  setHidden(hidden: boolean) {
    if (this.hidden === hidden) return;
    this.hidden = hidden;
    this.broadcastVis();
  }

  private broadcastVis() {
    for (const id of this.mesh?.ids() ?? []) if (this.mesh?.stateOf(id) !== PEER_GONE) this.mesh?.sendCtl(id, { t: 'vis', hidden: this.hidden });
  }

  // ---- leaving --------------------------------------------------------------------------------------

  /** Leave the room or the session (the others see this player go). */
  leave() {
    const wasLockstep = this.phase === 'session' && this.boot?.model === 'lockstep';
    if (this.boot && this.sink) for (let i = 0; i < this.boot.ids.length; i++) if (i !== this.boot.start.me) this.sink.peer(i, PEER_GONE);
    for (const id of this.mesh?.ids() ?? []) this.mesh?.sendCtl(id, { t: 'bye' });
    this.toRoom({ t: 'leave' });
    this.reset();
    this.error = '';
    this.changed();
    if (wasLockstep) this.deps.onEnd();
  }

  private fail(message: string) {
    for (const id of this.mesh?.ids() ?? []) this.mesh?.sendCtl(id, { t: 'bye' });
    this.reset();
    this.error = message;
    this.changed();
  }

  private reset() {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
    const ws = this.ws;
    this.ws = null;
    try { ws?.close(); } catch { /* closed */ }
    this.mesh?.closeAll();
    this.mesh = null;
    for (const r of this.rtcs.values()) r.close();
    this.rtcs.clear();
    this.lobby.clear();
    this.pingRows.clear();
    this.pendingBoot = null;
    this.queue = [];
    this.phase = 'idle';
    this.code = null;
    this.me = -1;
    this.hostId = -1;
    this.boot = null;
    this.announced = false;
  }

  /** The page is going away. */
  dispose() { if (this.phase !== 'idle') this.leave(); this.listeners.clear(); }
}

/** /api/room/<path> on our own origin. */
export function roomUrl(path: string, loc: { protocol: string; host: string } = location): string {
  return `${loc.protocol === 'https:' ? 'wss' : 'ws'}://${loc.host}/api/room/${path}`;
}
